import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  Stack,
  Divider
} from '@mui/material';
import { blueGrey, grey } from '@mui/material/colors';

const FOOTER_SECTIONS = [
  {
    title: '서비스',
    links: [
      { label: 'Daclair', url: '/services/daclair' },
      { label: 'ENV-REPORT', url: '/services/env-report' },
      { label: 'ENV-AD', url: '/services/env-ad' },
      { label: '탄소중립마켓 (VCM)', url: '/services/vcm' },
    ],
  },
  {
    title: '제품',
    links: [
      { label: '텀블러 세척기', url: '/products/tumblerwasher' },
      { label: '청소 카트', url: '/products/cleaningcart' },
    ],
  },
  {
    title: '회사 소개',
    links: [
      { label: 'Mission', url: '/company/mission' },
      { label: 'Sustainability', url: '/company/sustainability' },
      { label: '회사 위치', url: '/company/locations' },
      { label: '문의하기', url: '/company/contact' },
    ],
  },
  {
    title: '고객지원',
    links: [
      { label: '공지사항', url: '/notices' },
      { label: '캠페인', url: '/campaigns' },
      { label: '사용설명서', url: '/manuals' },
      { label: '마켓플레이스', url: null, external: 'https://ppi.g2b.go.kr:8914/sm/dm/sch/searchGoodsDetail.do?invGdsIdntNo=00027354' },
    ],
  },
];

const LEGAL_LINKS = [
  { label: '이용약관', url: '/terms/usage' },
  { label: '개인정보처리방침', url: '/terms/privacy' },
  { label: '위치정보 이용약관', url: '/terms/location' },
];

function FooterLink({ label, url, external, navigate }) {
  const sx = {
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover span': { color: '#ffffff' },
  };
  const text = (
    <Typography
      component="span"
      sx={{
        color: '#888888',
        fontSize: '13px',
        fontFamily: 'NotoSansKR',
        fontWeight: 400,
        transition: 'color 0.15s',
        lineHeight: 1.6,
      }}>
      {label}
    </Typography>
  );

  if (external) {
    return (
      <Link href={external} target="_blank" rel="noopener noreferrer" sx={sx}>
        {text}
      </Link>
    );
  }
  return (
    <Link onClick={() => navigate(url)} sx={sx}>
      {text}
    </Link>
  );
}

function Footer() {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: '#0a0a0a', pt: 7, pb: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* 브랜드 컬럼 */}
          <Grid size={{ xs: 12, sm: 12, md: 3 }}>
            <Stack spacing={2.5} alignItems={{ xs: 'center', md: 'center' }} direction="column">
              <Link href="/" sx={{ display: 'inline-block' }}>
                <img
                  src="/assets/icons/logo_transparent.png"
                  style={{ height: '22px', opacity: 0.85 }}
                  alt="Daclair logo"
                />
              </Link>
              <Typography
                sx={{
                  width:'100%',
                  color: '#666666',
                  fontSize: '13px',
                  fontFamily: 'NotoSansKR',
                  fontWeight: 400,
                  lineHeight: 1.8,
                  textAlign: 'center',
                  maxWidth: '220px',
                }}>
                사람과 환경을 우선으로 생각하는<br />
                ESG 솔루션 기업 다끌레어
              </Typography>
              <Typography
                sx={{
                  color: '#555555',
                  fontSize: '12px',
                  fontFamily: 'NotoSansKR',
                  lineHeight: 1.8,
                }}>
                contact@daclair.com
              </Typography>
            </Stack>
          </Grid>

          {/* 네비게이션 컬럼들 */}
          {FOOTER_SECTIONS.map((section) => (
            <Grid key={section.title} size={{ xs: 6, sm: 3, md: 2.25 }}>
              <Stack spacing={1.5}>
                <Typography
                  sx={{
                    color: '#cccccc',
                    fontSize: '13px',
                    fontFamily: 'NotoSansKR',
                    fontWeight: 700,
                    mb: 0.5,
                    letterSpacing: '0.5px',
                  }}>
                  {section.title}
                </Typography>
                {section.links.map((link) => (
                  <FooterLink
                    key={link.label}
                    label={link.label}
                    url={link.url}
                    external={link.external}
                    navigate={navigate}
                  />
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        {/* 하단 구분선 */}
        <Divider sx={{ borderColor: '#1e1e1e', mt: 6, mb: 3 }} />

        {/* 하단 로우 */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}>
          {/* 법적 링크 */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.label}
                onClick={() => navigate(link.url)}
                sx={{
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover span': { color: '#ffffff' },
                }}>
                <Typography
                  component="span"
                  sx={{
                    color: '#555555',
                    fontSize: '12px',
                    fontFamily: 'NotoSansKR',
                    transition: 'color 0.15s',
                  }}>
                  {link.label}
                </Typography>
              </Link>
            ))}
          </Stack>

          {/* 카피라이트 */}
          <Typography
            sx={{
              color: '#444444',
              fontSize: '12px',
              fontFamily: 'NotoSansKR',
              whiteSpace: 'nowrap',
            }}>
            ⓒ Daclair 2023–{new Date().getFullYear()}
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

export default Footer;
